import { 
  isEmailAPIConfigured, 
  sendEmail, 
  sendTestEmail,
  sendInvitationEmail,
  markEmailsSent,
  fetchApiKeyFromFirebase
} from '../models/emailModel';
import { updateEmailStats, logAdminActivity } from '../models/adminStatsModel';

// Load the email configuration
export const loadEmailConfig = async (setIsConfigured) => {
  try {
    const isConfigured = await fetchApiKeyFromFirebase();
    if (setIsConfigured) {
      setIsConfigured(isConfigured);
    }
    return isConfigured;
  } catch (error) {
    console.error('Error loading email config:', error);
    if (setIsConfigured) {
      setIsConfigured(false);
    }
    return false;
  }
};

// Check if email is configured
export const checkEmailConfig = () => {
  return isEmailAPIConfigured();
};

// Send a test email
export const testEmailConfig = async (email, setIsSending, setError) => {
  if (setIsSending) {
    setIsSending(true);
  }
  
  try {
    if (!email) {
      throw new Error('Email address is required');
    }
    
    if (!checkEmailConfig()) {
      throw new Error('Email API is not configured. Please ensure you are logged in.');
    }
    
    const result = await sendTestEmail(email);
    
    if (result.success) {
      return {
        success: true,
        data: {
          message: result.message || 'Test email sent successfully'
        }
      };
    } else {
      throw new Error(result.error || 'Failed to send test email');
    }
  } catch (error) {
    console.error('Test email error:', error);
    if (setError) {
      setError(error.message || 'Failed to send test email');
    }
    return { success: false, error: error.message };
  } finally {
    if (setIsSending) {
      setIsSending(false);
    }
  }
};

// Send email to specific recipients
export const sendEmailToRecipients = async (emailData, setIsSending, setError) => {
  if (setIsSending) {
    setIsSending(true);
  }
  
  try {
    const { recipients, subject, htmlContent, textContent, playStoreLink, appStoreLink } = emailData;
    
    if (!recipients || recipients.length === 0) {
      throw new Error('At least one recipient is required');
    }
    
    if (!subject) {
      throw new Error('Email subject is required');
    }
    
    if (!htmlContent && !textContent) {
      throw new Error('Email content is required');
    }
    
    if (!checkEmailConfig()) {
      throw new Error('Email API is not configured. Please ensure you are logged in.');
    }
    
    // Prepare email data for API
    const apiEmailData = {
      recipients,
      subject,
      content: htmlContent || textContent,
      playStoreLink: playStoreLink || '',
      appStoreLink: appStoreLink || ''
    };
    
    const result = await sendInvitationEmail(apiEmailData);
    
    if (result.success) {
      return {
        success: true,
        data: {
          message: `Email sent successfully to ${result.sentTo} recipients`,
          sentTo: result.sentTo,
          emailData: apiEmailData
        }
      };
    } else {
      throw new Error(result.error || 'Failed to send emails');
    }
  } catch (error) {
    console.error('Send email error:', error);
    if (setError) {
      setError(error.message || 'Failed to send emails');
    }
    return { success: false, error: error.message };
  } finally {
    if (setIsSending) {
      setIsSending(false);
    }
  }
};

// Send email to all wishlist subscribers
export const sendEmailToWishlist = async (wishlistEntries, emailData, setIsSending, setError) => {
  if (setIsSending) {
    setIsSending(true);
  }
  
  try {
    if (!wishlistEntries || wishlistEntries.length === 0) {
      throw new Error('No wishlist entries found');
    }
    
    if (!checkEmailConfig()) {
      throw new Error('Email API is not configured. Please ensure you are logged in.');
    }
    
    // Extract email addresses from wishlist entries
    const recipients = wishlistEntries.map(entry => entry.email);
    
    // Prepare email data for API
    const apiEmailData = {
      recipients,
      subject: emailData.subject,
      content: emailData.content || emailData.htmlContent || emailData.textContent,
      playStoreLink: emailData.playStoreLink || '',
      appStoreLink: emailData.appStoreLink || ''
    };
    
    // Send emails via API
    console.log('Sending emails to wishlist subscribers...', apiEmailData);
    const emailResult = await sendInvitationEmail(apiEmailData);
    
    if (emailResult.success) {
      // Mark emails as sent in Firebase
      console.log('Updating Firebase wishlist entries...');
      const updateResult = await markEmailsSent(wishlistEntries, {
        subject: emailData.subject,
        playStoreLink: emailData.playStoreLink,
        appStoreLink: emailData.appStoreLink
      });
      
      // Update email stats in admin section
      console.log('Updating admin email stats...');
      const statsResult = await updateEmailStats(recipients.length, {
        subject: emailData.subject,
        playStoreLink: emailData.playStoreLink,
        appStoreLink: emailData.appStoreLink
      });
      
      // Log email campaign activity
      await logAdminActivity({
        action: 'Email campaign sent',
        details: `${recipients.length} recipients`,
        type: 'email',
        campaignSubject: emailData.subject,
        recipientCount: recipients.length
      });
      
      if (updateResult.success) {
        return {
          success: true,
          data: {
            message: `Email sent successfully to ${recipients.length} wishlist subscribers and Firebase updated`,
            sentTo: recipients.length,
            firebaseUpdated: updateResult.updated,
            statsUpdated: statsResult.success,
            emailData: apiEmailData
          }
        };
      } else {
        // Email sent but Firebase update failed
        console.warn('Emails sent but Firebase update failed:', updateResult.error);
        return {
          success: true,
          data: {
            message: `Email sent successfully to ${recipients.length} wishlist subscribers (Firebase update failed: ${updateResult.error})`,
            sentTo: recipients.length,
            emailData: apiEmailData,
            statsUpdated: statsResult.success,
            warning: 'Firebase update failed'
          }
        };
      }
    } else {
      throw new Error(emailResult.error || 'Failed to send emails');
    }
  } catch (error) {
    console.error('Send to wishlist error:', error);
    if (setError) {
      setError(error.message || 'Failed to send emails to wishlist');
    }
    return { success: false, error: error.message };
  } finally {
    if (setIsSending) {
      setIsSending(false);
    }
  }
}; 